import React, { useEffect, useMemo, useState } from "react";
import {
  ExternalLink as ExternalLinkIcon,
  Check,
  Loader2,
  ShieldAlert,
} from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useTRPC } from "@/trpc";
import { Link } from "@tanstack/react-router";
import type { RequirementEligibility } from "@api/services/job-post/pure-requirements-evaluator";
import {
  parseRequirementsBlockedError,
  type RequirementsBlockedCause,
} from "./utils/requirements-blocked";

export const JobPostExternalAcceptDialog: React.FC<{
  jobId: string;
  eligibility?: RequirementEligibility;
  externalUrl: string;
  onClose: () => void;
  onSuccess: () => void;
  alreadyLiked?: boolean;
}> = ({
  jobId,
  eligibility,
  externalUrl,
  onClose,
  onSuccess,
  alreadyLiked = false,
}) => {
  const trpc = useTRPC();

  const [requirementsBlocked, setRequirementsBlocked] =
    useState<RequirementsBlockedCause | null>(null);

  // Memoized so the auto-fire useEffect below doesn't see a fresh
  // object reference each render and re-trigger setDecision.
  const localBlocker: RequirementsBlockedCause | null = useMemo(
    () =>
      eligibility?.blocked
        ? {
            code: "REQUIREMENTS_BLOCKED",
            failed: eligibility.failed,
          }
        : null,
    [eligibility],
  );
  const activeBlocker = requirementsBlocked ?? localBlocker;

  const {
    mutate: setDecision,
    isError,
    isPending,
    isSuccess,
  } = useMutation(
    trpc.jobPost.setDecision.mutationOptions({
      onSuccess: () => {
        onSuccess();
      },
      onError: (err) => {
        const blocked = parseRequirementsBlockedError(err);
        if (blocked) {
          setRequirementsBlocked(blocked);
        }
      },
    }),
  );

  // Immediately save interest when dialog opens (only if not already liked
  // AND not preemptively blocked — no point firing a doomed mutation).
  // `eligibility === null` means the credential context hasn't loaded;
  // wait one tick so we don't fire a mutation we know is doomed.
  useEffect(() => {
    if (!alreadyLiked && eligibility && !localBlocker) {
      setDecision({
        id: jobId,
        interested: true,
        decisionReasonIds: [],
      });
    }
  }, [jobId, setDecision, alreadyLiked, eligibility, localBlocker]);

  const handleGoToSite = (): void => {
    window.open(externalUrl, "_blank", "noopener,noreferrer");
    onClose();
  };

  const showLoading = isPending && !alreadyLiked && !activeBlocker;
  const showSuccess = (isSuccess || alreadyLiked) && !activeBlocker;
  // Only show when the failure isn't a structured requirements block —
  // those render their own dedicated surface.
  const showGenericError = isError && !alreadyLiked && !activeBlocker;

  const header = activeBlocker
    ? {
        icon: <ShieldAlert className="h-6 w-6 text-amber-600" />,
        title: "Requirements not met",
        description: "You don't yet qualify for this role — see below.",
      }
    : showLoading
      ? {
          icon: <Loader2 className="h-6 w-6 text-amber-600 animate-spin" />,
          title: "Saving...",
          description: "Recording your interest in this job...",
        }
      : showSuccess
        ? {
            icon: <Check className="h-6 w-6 text-green-600" />,
            title: "External Job",
            description:
              "This job is posted on an external job board. Click below to visit their site and complete your application.",
          }
        : {
            icon: <ExternalLinkIcon className="h-6 w-6 text-amber-600" />,
            title: "External Job",
            description:
              "This job is posted on an external job board. Click below to visit their site and complete your application.",
          };

  return (
    <Dialog open={true} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="[&.dialog-content]:max-w-[min(32rem,calc(100vw-2rem))] [&.dialog-content]:max-h-[calc(100dvh-2rem)] max-h-[calc(100dvh-2rem)] w-full overflow-y-auto rounded-2xl border-slate-200 bg-white text-slate-900 sm:max-w-lg sm:rounded-2xl">
        <DialogHeader>
          <div className="mx-auto bg-amber-50 p-4 rounded-2xl mb-4 w-16 h-16 flex items-center justify-center">
            {header.icon}
          </div>
          <DialogTitle className="text-center text-2xl font-semibold tracking-tight text-slate-900">
            {header.title}
          </DialogTitle>
          <DialogDescription className="text-center text-sm leading-relaxed text-slate-600 sm:px-4">
            {header.description}
          </DialogDescription>
        </DialogHeader>

        {activeBlocker && (
          <Alert className="my-4 border-amber-200 bg-amber-50">
            <ShieldAlert className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-900">
              You don&apos;t meet this role&apos;s requirements
            </AlertTitle>
            <AlertDescription className="text-amber-800 space-y-3">
              {activeBlocker.failed.length > 0 ? (
                <>
                  <p className="text-sm">
                    The following requirements must be met before you can apply:
                  </p>
                  <ul className="space-y-1.5">
                    {activeBlocker.failed.map((req) => (
                      <li
                        key={req.id}
                        className="flex flex-wrap items-start gap-2"
                      >
                        <Badge
                          variant="outline"
                          className="border-amber-300 bg-amber-100 text-amber-800 text-xs whitespace-normal text-left"
                        >
                          {req.displayLabel}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="text-sm">
                  Your profile is missing credentials required for this role.
                </p>
              )}
              <div className="pt-1">
                <Link
                  to="/provider/profile"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-900 underline hover:text-amber-700"
                  onClick={onClose}
                >
                  Go to My Profile
                  <ExternalLinkIcon size={12} />
                </Link>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {showGenericError && (
          <Alert variant="destructive" className="my-4">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              There was a problem saving your interest. You can still visit the
              external site to apply.
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter className="flex flex-col sm:flex-row justify-center gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto"
          >
            Close
          </Button>
          {!activeBlocker && (
            <Button
              type="button"
              onClick={handleGoToSite}
              disabled={showLoading}
              className="w-full sm:w-auto"
            >
              <ExternalLinkIcon className="h-4 w-4 mr-2" />
              Go to Application
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
